/**
 * Scenario-vergelijking: zelfde posten, tweede set hoeveelheden — roept /compare_scenarios aan.
 */
import { useEffect, useMemo, useState } from 'react';
import { GitCompare, Loader2, RotateCcw } from 'lucide-react';
import { api } from '../../lib/api';
import { humanizeAggregateLabel } from '../../lib/catalog-labels';
import { formatEuroNl, formatQuantityNl } from '../../lib/format-nl';
import type { ProjectSystemLine } from '../../types/project';

function systemsForCompare(lines: ProjectSystemLine[]) {
  return lines.map((s) => ({
    system_id: s.system_id,
    variant_id: s.variant_id,
    quantity: Number(s.quantity) || 0,
    ...(s.building_part?.trim() ? { building_part: s.building_part.trim() } : {}),
    ...(s.floor?.trim() ? { floor: s.floor.trim() } : {}),
    ...(s.room_number?.trim() ? { room_number: s.room_number.trim() } : {}),
  }));
}

export interface ScenarioComparePanelProps {
  projectId: string;
  tenantId: string;
  projectType: string;
  buildingType: string;
  indexYear: string;
  regionId?: string;
  systems: ProjectSystemLine[];
  inputClass: string;
  btnSecondary: string;
}

export function ScenarioComparePanel({
  projectId,
  tenantId,
  projectType,
  buildingType,
  indexYear,
  regionId,
  systems,
  inputClass,
  btnSecondary,
}: ScenarioComparePanelProps) {
  const baseKey = useMemo(
    () => systems.map((s) => `${s.line_id ?? ''}-${s.system_id}-${s.variant_id}`).join('|'),
    [systems]
  );

  const [altQty, setAltQty] = useState<Record<string, number>>({});

  useEffect(() => {
    const m: Record<string, number> = {};
    systems.forEach((s, i) => {
      m[`${s.line_id ?? i}`] = Number(s.quantity) || 0;
    });
    setAltQty(m);
    setResult(null);
    setErr(null);
  }, [baseKey]);

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<{
    totalA: number;
    totalB: number;
    diff: number;
    co2A?: number;
    co2B?: number;
  } | null>(null);

  const resetAlt = () => {
    const m: Record<string, number> = {};
    systems.forEach((s, i) => {
      m[`${s.line_id ?? i}`] = Number(s.quantity) || 0;
    });
    setAltQty(m);
    setResult(null);
    setErr(null);
  };

  const runCompare = async () => {
    setErr(null);
    setLoading(true);
    setResult(null);
    try {
      const sysA = systemsForCompare(systems);
      const sysB = systems.map((s, i) => {
        const key = `${s.line_id ?? i}`;
        const q = altQty[key] ?? (Number(s.quantity) || 0);
        return {
          system_id: s.system_id,
          variant_id: s.variant_id,
          quantity: q,
          ...(s.building_part?.trim() ? { building_part: s.building_part.trim() } : {}),
          ...(s.floor?.trim() ? { floor: s.floor.trim() } : {}),
          ...(s.room_number?.trim() ? { room_number: s.room_number.trim() } : {}),
        };
      });
      const raw = (await api.compareScenarios({
        project_a: { project_id: `${projectId}_a`, systems: sysA, project_type: projectType, building_type: buildingType },
        project_b: { project_id: `${projectId}_b`, systems: sysB, project_type: projectType, building_type: buildingType },
        index_year: indexYear,
        region_id: regionId,
        tenant_id: tenantId,
      })) as Record<string, unknown>;

      const pa = raw.project_a as Record<string, unknown> | undefined;
      const pb = raw.project_b as Record<string, unknown> | undefined;
      const totalA = Number(pa?.total ?? 0);
      const totalB = Number(pb?.total ?? 0);
      const co2A = pa?.total_co2_kg != null ? Number(pa.total_co2_kg) : undefined;
      const co2B = pb?.total_co2_kg != null ? Number(pb.total_co2_kg) : undefined;
      setResult({
        totalA,
        totalB,
        diff: totalB - totalA,
        co2A: Number.isFinite(co2A) ? co2A : undefined,
        co2B: Number.isFinite(co2B) ? co2B : undefined,
      });
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Vergelijking mislukt.');
    } finally {
      setLoading(false);
    }
  };

  if (systems.length === 0) return null;

  return (
    <section
      className="mt-8 rounded-xl border border-border bg-surface-card p-5 shadow-sm"
      aria-labelledby="scenario-compare-title"
    >
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 id="scenario-compare-title" className="flex items-center gap-2 text-base font-semibold text-text">
            <GitCompare className="h-5 w-5 text-primary" aria-hidden />
            Scenario&apos;s vergelijken
          </h3>
          <p className="mt-1 text-sm text-text-muted">
            <strong className="text-text">Basis</strong> = opgeslagen posten. Pas hieronder <strong className="text-text">alternatieve hoeveelheden</strong> aan
            (bijv. meer m² of extra cluster) en vergelijk totalen en CO₂-indicatie.
          </p>
        </div>
        <button type="button" className={`${btnSecondary} gap-2 text-sm`} onClick={() => resetAlt()}>
          <RotateCcw className="h-4 w-4" aria-hidden />
          Alternatief resetten
        </button>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border/80">
        <table className="w-full min-w-[520px] text-left text-sm">
          <thead className="border-b border-border bg-surface text-xs text-text-muted">
            <tr>
              <th className="px-3 py-2 font-medium">Post (systeem / variant)</th>
              <th className="px-3 py-2 font-medium">Hoeveelheid (basis)</th>
              <th className="px-3 py-2 font-medium">Hoeveelheid (alternatief)</th>
            </tr>
          </thead>
          <tbody>
            {systems.map((s, i) => {
              const key = `${s.line_id ?? i}`;
              return (
                <tr key={key} className="border-t border-border/60">
                  <td className="px-3 py-2 text-text">
                    <span className="text-sm text-text">{humanizeAggregateLabel(s.system_id)}</span>
                    <span className="text-text-muted"> · </span>
                    <span className="text-sm text-text">{humanizeAggregateLabel(s.variant_id)}</span>
                  </td>
                  <td className="px-3 py-2 tabular-nums text-text-muted">
                    {formatQuantityNl(Number(s.quantity) || 0)}
                  </td>
                  <td className="px-3 py-2">
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      className={`${inputClass} max-w-[7rem] py-1.5 text-sm`}
                      value={altQty[key] ?? 0}
                      onChange={(e) =>
                        setAltQty((prev) => ({ ...prev, [key]: Number(e.target.value) || 0 }))
                      }
                      aria-label={`Alternatieve hoeveelheid regel ${i + 1}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={loading}
          onClick={() => void runCompare()}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary-alt disabled:opacity-50"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <GitCompare className="h-4 w-4" aria-hidden />}
          Vergelijk scenario&apos;s
        </button>
      </div>

      {err && (
        <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-800 dark:bg-red-950/40 dark:text-red-100" role="alert">
          {err}
        </p>
      )}

      {result && (
        <div className="mt-4 space-y-3 rounded-lg border border-primary/20 bg-primary/[0.06] p-4 text-sm">
          <p className="font-semibold text-text">Resultaat vergelijking</p>
          <dl className="grid gap-2 sm:grid-cols-2">
            <div className="flex justify-between gap-2 rounded-md bg-surface-card/80 px-3 py-2">
              <dt className="text-text-muted">Totaal basis</dt>
              <dd className="font-medium tabular-nums text-text">{formatEuroNl(result.totalA)}</dd>
            </div>
            <div className="flex justify-between gap-2 rounded-md bg-surface-card/80 px-3 py-2">
              <dt className="text-text-muted">Totaal alternatief</dt>
              <dd className="font-medium tabular-nums text-text">{formatEuroNl(result.totalB)}</dd>
            </div>
            <div className="flex justify-between gap-2 rounded-md border border-primary/30 bg-primary/10 px-3 py-2 sm:col-span-2">
              <dt className="font-medium text-text">Verschil (alternatief − basis)</dt>
              <dd className="font-bold tabular-nums text-primary">
                {result.diff >= 0 ? '+' : ''}
                {formatEuroNl(result.diff)}
              </dd>
            </div>
            {result.co2A != null && result.co2B != null && (
              <div className="sm:col-span-2 text-xs text-text-muted">
                CO₂ indicatief: basis ca. {result.co2A.toFixed(0)} kg — alternatief ca. {result.co2B.toFixed(0)} kg (materiaalmodel).
              </div>
            )}
          </dl>
        </div>
      )}

      <div className="mt-4 rounded-lg border border-amber-200/80 bg-amber-50/80 px-3 py-2.5 text-xs leading-relaxed text-text-muted dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100/90">
        <strong className="text-text">Risico en bandbreedte:</strong> het prijsmodel is een{' '}
        <strong className="text-text">deterministische referentie</strong> (geen markttransactiedatabase). In de library horen
        elementen bij normale marktschommeling een bandbreedte te hebben; in de praktijk kan de uitkomst afwijken door
        uitvoering, inkoop en regio. Gebruik deze vergelijking om richting en ordegrootte te beoordelen, niet als
        garantieprijs.
      </div>

    </section>
  );
}
